import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileIcon, Image, FileText, MoreVertical, Download, Pencil, Trash2, Copy, FolderIcon, Eye, Search } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RenameDialog } from './RenameDialog'
import { PreviewDialog } from './PreviewDialog'
import type { Asset, AssetItem } from '@/types/assets'

interface AssetGridProps {
  items: AssetItem[]
  selectionMode?: boolean
  onAssetClick?: (asset: Asset) => void
  onFolderClick?: (folderId: number) => void
  onRenameAsset?: (assetId: number, name: string) => Promise<boolean>
  onDeleteAsset?: (assetId: number) => Promise<boolean>
  onRenameFolder?: (folderId: number, name: string) => Promise<boolean>
  onDeleteFolder?: (folderId: number) => Promise<boolean>
  onMove?: (assetId: number, folderId: number | null) => Promise<boolean>
  onCopyToProject?: (assetId: number, projectId: number, folderId?: number | null) => Promise<Asset | null>
  onCopyToOrganization?: (assetId: number, folderId?: number | null) => Promise<Asset | null>
  projects?: Array<{ id: number; name: string }>
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) {
    return <Image className="h-8 w-8 text-green-500" />
  }
  if (contentType === 'application/pdf') {
    return <FileText className="h-8 w-8 text-red-500" />
  }
  if (contentType.includes('spreadsheet') || contentType === 'text/csv') {
    return <FileText className="h-8 w-8 text-emerald-500" />
  }
  if (contentType.includes('document') || contentType === 'text/plain' || contentType === 'text/markdown') {
    return <FileText className="h-8 w-8 text-blue-500" />
  }
  return <FileIcon className="h-8 w-8 text-gray-500" />
}

export function AssetGrid({
  items,
  selectionMode = false,
  onAssetClick,
  onFolderClick,
  onRenameAsset,
  onDeleteAsset,
  onRenameFolder,
  onDeleteFolder,
  onMove,
  onCopyToProject,
  onCopyToOrganization,
  projects = []
}: AssetGridProps) {
  const { t } = useTranslation('assets')
  const [selectedItem, setSelectedItem] = useState<AssetItem | null>(null)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleRename = async (name: string) => {
    if (!selectedItem) return
    let success = false
    if (selectedItem.itemType === 'folder' && onRenameFolder) {
      success = await onRenameFolder(selectedItem.id, name)
    } else if (selectedItem.itemType === 'asset' && onRenameAsset) {
      success = await onRenameAsset(selectedItem.id, name)
    }
    if (success) {
      setShowRenameDialog(false)
      setSelectedItem(null)
    }
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    setIsDeleting(true)
    let success = false
    if (selectedItem.itemType === 'folder' && onDeleteFolder) {
      success = await onDeleteFolder(selectedItem.id)
    } else if (selectedItem.itemType === 'asset' && onDeleteAsset) {
      success = await onDeleteAsset(selectedItem.id)
    }
    setIsDeleting(false)
    if (success) {
      setShowDeleteDialog(false)
      setSelectedItem(null)
    }
  }

  const handleDownload = (asset: Asset) => {
    const link = document.createElement('a')
    link.href = asset.file_url
    link.download = asset.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderFolderCard = (item: AssetItem & { itemType: 'folder' }) => (
    <Card
      key={`folder-${item.id}`}
      className="group relative cursor-pointer hover:bg-accent transition-colors p-4"
      onClick={() => onFolderClick?.(item.id)}
    >
      <div className="flex flex-col items-center text-center gap-3">
        <div className="h-16 w-16 flex items-center justify-center">
          <FolderIcon className="h-10 w-10 text-blue-500" />
        </div>
        <div className="w-full min-w-0">
          <p className="font-medium truncate" title={item.name}>
            {item.name}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('item', { count: item.assets_count + item.children_count })}
          </p>
        </div>
      </div>
      {!selectionMode && (onRenameFolder || onDeleteFolder) && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8"
                onClick={e => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onRenameFolder && (
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation()
                  setSelectedItem(item)
                  setShowRenameDialog(true)
                }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('buttons.rename')}
                </DropdownMenuItem>
              )}
              {onDeleteFolder && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedItem(item)
                      setShowDeleteDialog(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('buttons.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </Card>
  )

  const renderAssetCard = (item: AssetItem & { itemType: 'asset' }) => (
    <Card
      key={`asset-${item.id}`}
      className={`group relative overflow-hidden p-4 ${
        selectionMode ? 'cursor-pointer hover:ring-2 hover:ring-primary' : ''
      }`}
      onClick={() => selectionMode && onAssetClick?.(item)}
    >
      <div className="flex flex-col items-center text-center gap-3">
        <div
          className={`h-16 w-16 flex items-center justify-center rounded bg-muted overflow-hidden relative ${
            !selectionMode ? 'cursor-pointer group/thumb' : ''
          }`}
          onClick={(e) => {
            if (!selectionMode) {
              e.stopPropagation()
              setPreviewAsset(item)
              setShowPreviewDialog(true)
            }
          }}
        >
          {item.is_image && item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            getFileIcon(item.content_type)
          )}

          {/* Preview overlay - shows on hover */}
          {!selectionMode && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
              <Search className="h-6 w-6 text-white" />
            </div>
          )}
        </div>
        <div className="w-full min-w-0">
          <p className="font-medium truncate" title={item.name}>
            {item.name}
          </p>
          <p className="text-sm text-muted-foreground">
            {item.human_byte_size}
          </p>
        </div>
      </div>
      {!selectionMode && (onRenameAsset || onDeleteAsset || onMove || onCopyToProject || onCopyToOrganization) && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8"
                onClick={e => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setPreviewAsset(item)
                setShowPreviewDialog(true)
              }}>
                <Eye className="h-4 w-4 mr-2" />
                {t('buttons.preview')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload(item)}>
                <Download className="h-4 w-4 mr-2" />
                {t('buttons.download')}
              </DropdownMenuItem>

              {onRenameAsset && (
                <DropdownMenuItem onClick={() => {
                  setSelectedItem(item)
                  setShowRenameDialog(true)
                }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('buttons.rename')}
                </DropdownMenuItem>
              )}

              {onCopyToProject && projects.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('buttons.copyToProject')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-48 overflow-auto">
                    {projects.map(project => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => onCopyToProject(item.id, project.id)}
                      >
                        {project.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {onCopyToOrganization && (
                <DropdownMenuItem onClick={() => onCopyToOrganization(item.id)}>
                  <Copy className="h-4 w-4 mr-2" />
                  {t('buttons.copyToOrganization')}
                </DropdownMenuItem>
              )}

              {onDeleteAsset && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => {
                      setSelectedItem(item)
                      setShowDeleteDialog(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('buttons.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </Card>
  )

  const deleteTitle = selectedItem?.itemType === 'folder'
    ? t('dialogs.deleteFolder.title')
    : t('dialogs.deleteFile.title')
  const deleteDescription = selectedItem?.itemType === 'folder'
    ? t('dialogs.deleteFolder.description', { name: selectedItem?.name })
    : t('dialogs.deleteFile.description', { name: selectedItem?.name })

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {items.map(item =>
          item.itemType === 'folder' ? renderFolderCard(item) : renderAssetCard(item)
        )}
      </div>

      {/* Preview Dialog */}
      <PreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        asset={previewAsset}
      />

      {/* Rename Dialog */}
      <RenameDialog
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        currentName={selectedItem?.name || ''}
        onSubmit={handleRename}
        isFolder={selectedItem?.itemType === 'folder'}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('buttons.deleting') : t('buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
